import { instance } from 'common/axios/axios';
import { getApiUrl } from 'common/axios/getApiUrl';

/** 장소 사진 배치 조회.
 *
 * 예전에는 이 요청과 응답 파싱이 화면마다 복사돼 있었습니다(9곳). 그러다 보니
 * Foursquare 카테고리 아이콘을 걸러내는 규칙이 두 화면에만 있고 나머지는 빠져,
 * 같은 API 를 쓰는데 어떤 화면은 아이콘을 사진처럼 보여줬습니다.
 * 요청과 정리는 여기 한 곳에서만 하고, 각 화면은 받은 값을 자기 모양으로 합칩니다.
 */

export interface PlacePhoto {
    photoUrl?: string;
    photos?: string[];
    rating?: number;
    openingHours?: string;
    openingHoursList?: string[];
}

/** Foursquare 카테고리 아이콘 — 장소 사진이 아니라 분류 픽토그램입니다. */
export const isPlaceholderPhoto = (url?: unknown): boolean =>
    typeof url === 'string' && url.includes('4sqi.net/img/categories');

/** 항목이 이미 들고 있는 사진 URL. 아이콘이면 없는 것으로 봅니다. */
export const pickPhotoUrl = (p: any): string | undefined => {
    const url = p?.photoUrl || p?.imageUrl;
    if (typeof url !== 'string' || !url) return undefined;
    return isPlaceholderPhoto(url) ? undefined : url;
};

/** 서버가 한 번에 받는 최대 개수. 더 보내면 앞에서 잘려 나머지는 조용히 빠집니다. */
const MAX_PER_CALL = 20;

/** placeIds 로 사진·평점·영업시간을 받아옵니다. 실패해도 화면은 성립하므로 빈 값을 돌려줍니다.
 *
 *  상한을 넘는 목록은 끊어서 여러 번 부릅니다. 예전에는 그냥 한 번에 보내서,
 *  20개를 넘기면 나머지는 응답이 없는 채로 "받아온 것"으로 처리돼
 *  목록에 빈 네모가 남았습니다. */
export async function fetchPlacePhotos(ids: (string | null | undefined)[]): Promise<Record<string, PlacePhoto>> {
    const list = Array.from(new Set(ids.filter(Boolean).map(String)));
    if (list.length === 0) return {};

    const chunks: string[][] = [];
    for (let i = 0; i < list.length; i += MAX_PER_CALL) {
        chunks.push(list.slice(i, i + MAX_PER_CALL));
    }

    const out: Record<string, PlacePhoto> = {};
    const results = await Promise.all(chunks.map(async (chunk) => {
        try {
            const res = await instance.get(getApiUrl('placePhotos'), {
                params: { placeIds: chunk.join(',') },
            });
            return (res?.data?.data || {}) as Record<string, PlacePhoto>;
        } catch {
            return {} as Record<string, PlacePhoto>;
        }
    }));

    for (const raw of results) {
        for (const [id, v] of Object.entries(raw)) {
            const first = v?.photos?.find(u => !isPlaceholderPhoto(u));
            out[id] = { ...v, photoUrl: pickPhotoUrl(v) || first };
        }
    }
    return out;
}
